import React, { useEffect, useState } from 'react';
import { Rate, Carousel, App } from 'antd';
import styles from './index.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import ProductBanner from '@/components/ProductBanner/ProductBanner';
import { createChatRoom } from '@/api/message';
import OrderDialog, { OrderDialogMode, OrderDialogOrder } from '@/components/OrderDialog/OrderDialog';
import { getProductCategoryAllList } from '@/api/products';
import { getEmergencyServiceById } from '@/api/emergencyServiceApi';
import { useAppSelector } from '@/store/hooks';

import icon1 from '@/assets/images/组 2387@2x.png';
import icon2 from '@/assets/images/路径 1659@2x.png';
import pic1 from '@/assets/images/生成美国电商店铺头像 (49)@2x.png';

// 用户信息类型
type UserInfo = {
  user_id: string;
  merchant_id: string;
  first_name: string;
  last_name: string;
  nick_name: string;
  username: string;
  email: string;
  phone: string | null;
  photo: string;
  gender: number;
  birthday: string | null;
  level: number;
  is_delete: boolean;
  create_time: string;
  update_time: string;
};

// 紧急服务详情类型
type EmergencyServiceDetail = {
  emergency_service_id: string | number;
  service_category: number[];
  service_type: number;
  problem_description: string;
  fixed_price: number;
  budget: number;
  city_state: number;
  address: string;
  phone: string;
  appointment_time: string;
  images: any[];
  created_at: string;
  distance: number;
  user: UserInfo; // 添加用户信息字段
};

// 与 NearbyOrders 中一致的路径名获取方法（输入为级联 options）
const getCategoryPathLabel = (categories: any[], path: number[]): string => {
  if (!Array.isArray(categories) || categories.length === 0 || !Array.isArray(path)) return '';
  let level: any[] | undefined = categories;
  const labels: string[] = [];
  for (const val of path) {
    if (!Array.isArray(level)) break;
    const node: any = (level as any[]).find((n: any) => n.value === val);
    if (!node) break;
    labels.push(node.label);
    level = node.children;
  }
  return labels.length > 0 ? labels.join(' / ') : path.join('/');
};

// 将后端分类结构转换为级联 options 结构（最小实现）
const toCascaderOptions = (data: any[] = []): any[] =>
  data.map((item) => ({
    value: item.product_category_id,
    label: item.product_category_name,
    children: Array.isArray(item.child) ? toCascaderOptions(item.child) : undefined,
  }));

const ServiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { message } = App.useApp();

  // 获取用户定位信息
  const latitude = useAppSelector((state) => state.user.latitude);
  const longitude = useAppSelector((state) => state.user.longitude);

  const [value, setValue] = useState(5);
  const [imgList, setImgList] = useState<string[]>([]);
  const [ownerUserId, setOwnerUserId] = useState<string>('');
  const [order, setOrder] = useState<EmergencyServiceDetail | null>(null);
  const [serviceCategories, setServiceCategories] = useState<any[]>([]);

  // 弹窗
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<OrderDialogMode>('bidding');
  const [currentOrder, setCurrentOrder] = useState<OrderDialogOrder | null>(null);

  // 轮播切换（移动端）
  const onChange = (_currentSlide: number) => {};

  // 根据ID获取紧急服务详情
  const fetchEmergencyServiceDetail = async (id: string) => {
    try {
      // 构建请求参数，包含经纬度信息
      const requestParams = {
        emergency_service_id: id,
        user_latitude: latitude,
        user_longitude: longitude
      };
      
      console.log('请求参数:', requestParams);
      
      const res = await getEmergencyServiceById(requestParams);
      console.log('紧急服务详情API返回:', res);
      
             if (res && res.code === 200) {
         const serviceDetail = res.data;
         setOrder(serviceDetail);
         
         // 处理图片列表
         if (serviceDetail.images && Array.isArray(serviceDetail.images)) {
           const imgs = serviceDetail.images
             .map((img: any) => img.product_media_file_url || '')
             .filter(Boolean);
           setImgList(imgs);
         }
         
       } else {
      }
    } catch (error) {

    }
  };

  // 读取URL参数并获取详情
  useEffect(() => {
    const id = params.id as string;
    if (id) {
      console.log('获取紧急服务详情，ID:', id);
      fetchEmergencyServiceDetail(id);
    }
  }, [params.id]);

  // 拉取分类数据（用于标题显示分类路径）
  useEffect(() => {
    getProductCategoryAllList(2).then((res: any) => {
      if (res?.code === 200) {
        setServiceCategories(toCascaderOptions(res.data || []));
      }
    });
  }, []);

  // 联系客服
  const handleCreateChatRoom = () => {
    if (!order?.user?.user_id) {
      message.error('User not found');
      return;
    }
    createChatRoom({ member_id: order.user.user_id }).then((res: any) => {
      if (res.code === 200) {
        navigate(`/chatList?chatRoomId=${res.data.room_id}`);
      } else {
        message.error(res.message);
      }
    });
  };

  const openDialog = (mode: OrderDialogMode) => {
    if (!order) return;
    setDialogMode(mode);
    setCurrentOrder({
      imageUrl: imgList[0] || '/api/placeholder/640/420?text=No+Image',
      title: order.problem_description || 'Service Order',
             distanceMiles: `${order.distance ? order.distance.toFixed(2) : '0.00'} Miles From You`,
      appointment_time: order.appointment_time || '',
      address: order.address || '',
      mechanism: mode === 'bidding' ? 'Bidding' : 'Fixed Price',
      problemDescription: order.problem_description || '',
             price: mode === 'fixed' ? (order.fixed_price || 0) : (order.budget || 0),
      emergency_service_id: order.emergency_service_id?.toString() || '',
    });
    setDialogOpen(true);
  };



  // 添加调试信息
  console.log('ServiceDetail 当前坐标:', { latitude, longitude });

  return (
    <div className={styles['product-detail']}>
      <Carousel afterChange={onChange} className={styles.Carousel} autoplay>
        {imgList?.map((item, index) => (
          <div key={index}>
            <img src={item} alt="" className={styles.bannerBoxMiddleImg} />
          </div>
        ))}
      </Carousel>

      {/* 详情页头部（复用 ProductDetail 结构） */}
      <div className={styles['product-detail-top-box']}>
      <div className={styles['product-name-box']}>
           <img 
             src={order?.user?.photo} 
             alt="" 
             className={styles['product-name-img']}
             style={{ borderRadius: '50%', width: '60px', height: '60px', objectFit: 'cover' }}
           />
           <div className={styles['product-name-right']}>
             <div className={styles['product-name-text1']}>
               {`${order?.user?.first_name}${order?.user?.last_name}`}
             </div>
             <div className={styles['product-name-text2']}>
               {order?.user?.user_id}
             </div>
           </div>
         </div>
        <div className={styles['product-btn-box']}>
          <div className={styles['product-btn1']} onClick={handleCreateChatRoom}>
            <img src={icon1} alt="" />
            Contact customer service
          </div>
        </div>
      </div>

      <div className={styles['product-detail-content']}>
        <div className={styles['product-detail-content-left']}>
          <div className={styles['product-detail-content-ProductBanner']}>
            <ProductBanner imgList={imgList} />
          </div>
        </div>
        <div className={styles['product-detail-content-right']}>
          <h2 className={styles['service-title']}>
            {order?.service_category ? getCategoryPathLabel(serviceCategories, order.service_category as any) : 'Service Detail'}
          </h2>
          <div className={styles['distanceRow']}>
            <span className={styles['distanceIcon']} />
            <span className={styles['distanceText']}>
              {order?.distance ? `${order.distance.toFixed(2)} Miles From You` : ''}
            </span>
          </div>
          <ul className={styles['metaList']}>
            <li className={styles['metaItem']}>
              <span className={styles['metaLabel']}>Address:</span>
              {/* <span className={styles['metaValue']}>{order?.address}</span> */}
              {order?.address ? (order.address.slice(0, 15) + '****') : 'Address N/A'}

            </li>
            <li className={styles['metaItem']}>
              <span className={styles['metaLabel']}>Appointment Time:</span>
              <span className={styles['metaValue']}>{order?.appointment_time}</span>
            </li>
            {/* <li className={styles['metaItem']}>
              <span className={styles['metaLabel']}>Bidding Mechanism:</span>
              <span className={styles['metaValue']}>{order?.bidding_mechanism === 2 ? 'Optimal Price' : 'Fixed Price'}</span>
            </li> */}
            {/* <li className={styles['metaItem']}>
              <span className={styles['metaLabel']}>Phone:</span>
              <span className={styles['metaValue']}>{order?.phone}</span>
            </li> */}
            <li className={styles['metaItem']}>
              <span className={styles['metaLabel']}>Problem Description:</span>
              <span className={styles['metaValue']}>{order?.problem_description}</span>
            </li>
          </ul>

                     <div className={styles['priceRow']}>
             <span className={styles['priceLabel']}>Fixed Price:</span>
             <span className={styles['priceValue']}>${order?.fixed_price || 0}</span>
           </div>
           <div className={styles['priceRow']}>
             <span className={styles['priceLabel']}>Budget:</span>
             <span className={styles['priceValue']}>${order?.budget || 0}</span>
           </div>
          <div className={styles['actions']}>
            <button className={styles['bidBtn']} onClick={() => openDialog('bidding')}>Bidding</button>
            <button className={styles['fixedBtn']} onClick={() => openDialog('fixed')}>Fixed Price</button>
          </div>
        </div>
      </div>

      {currentOrder && (
        <OrderDialog
          open={dialogOpen}
          mode={dialogMode}
          order={currentOrder}
          onCancel={() => setDialogOpen(false)}
          onConfirm={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default ServiceDetail;


